/**
 * Contrato dos lançamentos de residência.
 *
 * Papel: aqui ficam `novoLancamentoResidenciaSchema`, que o formulário e a
 * Function usam para conferir um lançamento, `lancamentoResidenciaSchema` e
 * `lancamentosDasResidenciasSchema`, que descrevem o que
 * `GET /api/residencias/lancamentos` devolve, e as duas listas fechadas de onde
 * saem os enums do banco.
 *
 * Como usar: `CATEGORIAS_LANCAMENTO_RESIDENCIA` e `STATUS_LANCAMENTO_RESIDENCIA`
 * dão os valores dos enums `categoria_lancamento_residencia` e
 * `status_lancamento_residencia` do Postgres; mudar qualquer um dos dois arrays
 * pede migration, e o banco recusa com 500 o valor que só existir aqui.
 */
import { z } from 'zod';

import { dataSchema, idSchema, textoObrigatorio, textoOpcional } from './comum';
import { dinheiroComPadraoZeroSchema } from './formatos';

/**
 * As categorias de um lançamento de residência, na ordem em que o `<select>` do
 * formulário as oferece.
 *
 * A ordem é a das categorias de Residências na origem, e a primeira é a que o
 * lançamento novo traz marcada: trocar a posição de um valor muda o padrão de
 * todo lançamento criado sem que a pessoa toque no campo.
 */
export const CATEGORIAS_LANCAMENTO_RESIDENCIA = [
  'manutencao_preventiva',
  'reparo',
  'taxa_condominial',
  'fornecedor',
  'outros',
] as const;

export type CategoriaLancamentoResidencia = (typeof CATEGORIAS_LANCAMENTO_RESIDENCIA)[number];

/**
 * As duas situações de um lançamento de residência.
 *
 * O selo da tabela só fica verde em `concluido`; um valor acrescentado aqui sai
 * âmbar, como tudo o que não é "Concluído" no selo da origem.
 */
export const STATUS_LANCAMENTO_RESIDENCIA = ['pendente', 'concluido'] as const;

export type StatusLancamentoResidencia = (typeof STATUS_LANCAMENTO_RESIDENCIA)[number];

/**
 * Um lançamento como o formulário o envia e a Function o confere.
 *
 * `residenciaId` nulo ou ausente vira `null`, que é "Ambas / não se aplica", e
 * não uma terceira casa. `valor` aceita número e texto, e vazio, nulo e ausente
 * valem zero; o teto de `1e10` é o de `numeric(12,2)`, e um valor acima dele
 * passaria daqui e voltaria do banco como 500, em vez de 400 com a mensagem.
 *
 * Descrição e observação têm teto de 500 caracteres, que é o menor limite que
 * nenhum lançamento real alcança.
 */
export const novoLancamentoResidenciaSchema = z.object({
  data: dataSchema,
  residenciaId: idSchema.nullish().transform((valor) => valor ?? null),
  descricao: textoObrigatorio('Descrição', 500),
  categoria: z.enum(CATEGORIAS_LANCAMENTO_RESIDENCIA, { message: 'Categoria desconhecida.' }),
  valor: dinheiroComPadraoZeroSchema.refine((valor) => Math.abs(valor) < 1e10, {
    message: 'Valor inválido.',
  }),
  status: z.enum(STATUS_LANCAMENTO_RESIDENCIA, { message: 'Situação desconhecida.' }),
  observacao: textoOpcional(500),
});

export type NovoLancamentoResidencia = z.infer<typeof novoLancamentoResidenciaSchema>;

/**
 * Uma casa como o lançamento e a lista de opções a trazem.
 *
 * Tem só o que a tela mostra — o título do bloco, o subtítulo e o texto da
 * opção saem de `nome`, `cidade` e `uf` —, porque quem a recebe é qualquer
 * pessoa com `ver` em `residencias`, que pode não alcançar o cadastro de casas.
 */
export const casaDoLancamentoSchema = z.object({
  id: idSchema,
  nome: z.string(),
  cidade: z.string(),
  uf: z.string(),
});

export type CasaDoLancamento = z.infer<typeof casaDoLancamentoSchema>;

/**
 * Um lançamento como a API o devolve.
 *
 * `residencia` vem aninhada, e não como identificador solto, para a tela saber
 * o nome de uma casa que não está entre as de topo — a que passou a ficar dentro
 * de outra depois do lançamento. É nula em "Ambas / não se aplica".
 */
export const lancamentoResidenciaSchema = z.object({
  id: idSchema,
  data: dataSchema,
  residencia: casaDoLancamentoSchema.nullable(),
  descricao: z.string(),
  categoria: z.enum(CATEGORIAS_LANCAMENTO_RESIDENCIA),
  valor: z.number(),
  status: z.enum(STATUS_LANCAMENTO_RESIDENCIA),
  observacao: z.string().nullable(),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type LancamentoResidencia = z.infer<typeof lancamentoResidenciaSchema>;

/**
 * O que a tela de Residências lê de uma vez: as casas de topo e os lançamentos.
 *
 * `casas` vem junto porque quem tem `residencias` sem `cadastros-base` recebe
 * 403 de `/api/residencias`, e sem elas a tela não teria título de bloco nem
 * opção de `<select>`. A ordem de `casas` não é contrato: a tela põe a fazenda
 * na frente e ordena as outras. A de `lancamentos` é a de criação.
 */
export const lancamentosDasResidenciasSchema = z.object({
  casas: z.array(casaDoLancamentoSchema),
  lancamentos: z.array(lancamentoResidenciaSchema),
});

export type LancamentosDasResidencias = z.infer<typeof lancamentosDasResidenciasSchema>;
